/**
 * PATH: packages/email-hub/src/feature/components/ThreadMessage.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-THREADMESSAGE-001
 * PURPOSE: One message block in the stacked-thread reader. Renders a
 *          colour-accented card (left border + header dot keyed to the
 *          message's thread colour), a compact sender/date/attachment
 *          header that collapses/expands the block, the message's NEW
 *          content (quoted history trimmed via `splitNewVsQuoted` and tucked
 *          behind a "Show quoted text" toggle), and this message's own
 *          attachment chips.
 * WHY:     Gmail-style stacked thread — each conversation message is its own
 *          block with a clear identity, so the duplicated chain that every
 *          reply embeds isn't shown N times and attachments tie back to the
 *          message they arrived on (see threadColors.ts).
 * RELATED:
 *   - lib/quoteTrim.ts (new/quoted split)
 *   - lib/threadColors.ts (per-message colour)
 *   - components/EmailThread.tsx (orchestrator + scroll-to refs)
 *   - components/AttachmentList.tsx (per-message chips)
 *   - @quotech/ui EmailHtmlBody (canonical body renderer)
 */

import { useMemo, useState, type ReactNode } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Forward,
  Paperclip,
  Reply,
  ReplyAll,
} from 'lucide-react'
import { EmailHtmlBody, RecipientList } from '@quotech/ui'
import { cn } from '../../_internal/lib/utils'
import { formatFullDate, selectVisibleAttachments } from '../lib/utils'
import { splitNewVsQuoted } from '../lib/quoteTrim'
import type { MessageFindMatches } from '../lib/findInThread'
import type { ThreadColor } from '../lib/threadColors'
import type { Email } from '../hooks/useEmails'
import AttachmentList from './AttachmentList'
import {
  VerificationCodeCard,
  verificationCodeSignalFor,
} from '../action-signals'

const AVATAR_RING = 'ring-2 ring-white dark:ring-neutral-900'

function ThreadReplyButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent"
    >
      {icon}
      {label}
    </button>
  )
}

function getInitials(name: string): string {
  return (name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export interface ThreadMessageProps {
  email: Email
  /** Per-message colour from the thread palette. */
  color: ThreadColor
  /** Whether the block body is expanded. Controlled by the orchestrator so
   *  the opened + latest messages can default open and the rest collapsed. */
  expanded: boolean
  onToggleExpand: () => void
  /** Body zoom (full reader passes its zoom; preview pane omits → 1). */
  zoom?: number
  /** Open the shared preview modal at this message's local attachment index. */
  onPreview: (emailId: string, localIndex: number) => void
  /** Scroll-to ref registration (the attachment bar scrolls here). */
  setRef?: (el: HTMLDivElement | null) => void
  /** Highlight when this message's attachment group is selected / in view. */
  isActive?: boolean
  /** Open the compose dock seeded from THIS message (Gmail-style per-message
   *  reply). Omitted → the action row is hidden. */
  onReply?: (intent: 'reply' | 'reply_all' | 'forward') => void
  /** True while the detail GET (full body_html) is still in flight for
   *  THIS message. Gates the plain-text fallback so the operator sees a
   *  body skeleton instead of a one-line `body_preview` flash that gets
   *  replaced ~a second later (read like a rendering glitch). */
  bodyLoading?: boolean
  /** Find-in-thread (G5): this message's match counts while a find query
   *  is live. Renders the per-message "N matches" chip in the header. */
  findMatches?: MessageFindMatches
  /** Find-in-thread (G5): force the quoted-history section open because a
   *  match lives inside it (hidden quoted text has no DOM to highlight).
   *  The user's own "Show/Hide quoted text" toggle resumes when this
   *  drops back to false. */
  forceShowQuoted?: boolean
}

export default function ThreadMessage({
  email,
  color,
  expanded,
  onToggleExpand,
  zoom,
  onPreview,
  setRef,
  isActive,
  onReply,
  bodyLoading,
  findMatches,
  forceShowQuoted,
}: ThreadMessageProps) {
  const { newHtml, quotedHtml } = useMemo(
    () => splitNewVsQuoted(email.body_html),
    [email.body_html],
  )
  const visible = useMemo(
    () => selectVisibleAttachments(email.attachments),
    [email.attachments],
  )
  const verificationCode = verificationCodeSignalFor(email)
  const [dismissedSignals, setDismissedSignals] = useState<Set<string>>(
    () => new Set(),
  )
  const hasNew = !!newHtml && !!newHtml.trim()
  const hasQuoted = !!quotedHtml && !!quotedHtml.trim()
  // Pure-quoted messages (no new content above the boundary) show their
  // quoted body straight away so the block is never blank.
  const [showQuoted, setShowQuoted] = useState(!hasNew && hasQuoted)
  // Find-in-thread: a hit inside the quoted tail forces the section open
  // (rendered = highlightable). Purely additive — the local toggle state
  // is untouched, so closing the find restores the user's own choice.
  const quotedVisible = showQuoted || !!forceShowQuoted

  const senderName =
    email.from_name || (email.from_address ?? '').split('@')[0] || 'Unknown sender'
  const attCount = visible.length

  return (
    <div
      ref={setRef}
      data-message-id={email.id}
      data-testid="thread-message"
      className={cn(
        'border-l-4 rounded-r-md scroll-mt-4 transition-shadow',
        color.border,
        isActive && cn('ring-2 ring-offset-1 ring-offset-background', color.ring),
      )}
    >
      {/* Header — click to collapse/expand */}
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        className={cn(
          'w-full flex items-start gap-3 text-left px-3 py-2 rounded-tr-md',
          color.headerBg,
        )}
      >
        <span className="mt-0.5 shrink-0 text-muted-foreground">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
        <span
          className={cn(
            'h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0',
            color.dot,
            AVATAR_RING,
          )}
        >
          {getInitials(senderName)}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2 flex-wrap">
            <span className={cn('font-semibold text-sm truncate', color.text)}>
              {senderName}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              &lt;{email.from_address}&gt;
            </span>
          </span>
          {!expanded && (
            <span className="block text-xs text-muted-foreground truncate">
              {email.body_preview || ''}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          {findMatches && findMatches.total > 0 && (
            <span
              data-testid="thread-find-chip"
              className="inline-flex items-center rounded-full bg-yellow-200/80 px-1.5 py-0.5 text-[10px] font-medium text-yellow-900 dark:bg-yellow-500/25 dark:text-yellow-200"
              title={`${findMatches.total} find match${findMatches.total === 1 ? '' : 'es'} in this message`}
            >
              {findMatches.total} match{findMatches.total === 1 ? '' : 'es'}
            </span>
          )}
          {attCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {attCount}
            </span>
          )}
          <span className="tabular-nums whitespace-nowrap">
            {formatFullDate(email.received_at)}
          </span>
        </span>
      </button>

      {/* Recipients meta — OUTSIDE the toggle button so each To/Cc/Bcc
          entry can be its own interactive control (tap reveals the
          address). Operator (2026-06-16): "I was CC'ed, I can't click my
          name to see which email it CC'd to." The two leading spacers
          mirror the button's chevron (w-4) + avatar (w-8) columns so the
          list aligns under the sender without magic offsets. */}
      {expanded && (
        <div className="flex gap-3 px-3 pb-1.5">
          <span className="w-4 shrink-0" aria-hidden />
          <span className="w-8 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 space-y-0.5">
            <RecipientList
              label="To"
              recipients={email.to_addresses}
              emptyText="me"
              max={3}
              className="block text-xs text-muted-foreground"
            />
            {email.cc_addresses && email.cc_addresses.length > 0 && (
              <RecipientList
                label="Cc"
                recipients={email.cc_addresses}
                max={3}
                className="block text-xs text-muted-foreground"
              />
            )}
            {email.bcc_addresses && email.bcc_addresses.length > 0 && (
              <RecipientList
                label="Bcc"
                recipients={email.bcc_addresses}
                max={3}
                className="block text-xs text-muted-foreground"
              />
            )}
          </div>
        </div>
      )}

      {expanded && (
        <div className="px-3 pb-3 pt-2">
          {verificationCode && !dismissedSignals.has(verificationCode.kind) && (
            <VerificationCodeCard
              emailId={email.id}
              signal={verificationCode}
              onRejected={() =>
                setDismissedSignals((current) => new Set(current).add(verificationCode.kind))
              }
            />
          )}
          {hasNew && (
            <EmailHtmlBody
              html={newHtml}
              text={email.body_text || email.body_preview}
              attachments={email.attachments}
              zoom={zoom}
              fitToWidth
            />
          )}

          {hasQuoted && (
            <div className={cn(hasNew && 'mt-2')}>
              <button
                type="button"
                onClick={() => setShowQuoted((v) => !v)}
                disabled={!!forceShowQuoted}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-muted-foreground hover:bg-accent border border-border disabled:cursor-not-allowed disabled:opacity-60"
                title={
                  forceShowQuoted
                    ? 'Quoted text is shown because it contains a find match'
                    : quotedVisible
                      ? 'Hide quoted text'
                      : 'Show quoted text'
                }
              >
                <span className="leading-none tracking-widest">···</span>
                {quotedVisible ? 'Hide quoted text' : 'Show quoted text'}
              </button>
              {quotedVisible && (
                <EmailHtmlBody
                  className="mt-2 border-l-2 border-border pl-3 opacity-90"
                  html={quotedHtml}
                  attachments={email.attachments}
                  zoom={zoom}
                  fitToWidth
                />
              )}
            </div>
          )}

          {/* Fallback: neither new nor quoted HTML resolved. While the
              detail fetch is still in flight show a body skeleton - the
              old behaviour painted the one-line `body_preview`, then the
              real HTML replaced it ~a second later, which read like a
              rendering glitch on every email open. Once loading settles
              (or there genuinely is no HTML) render the plain text so
              the message is never empty. */}
          {!hasNew && !hasQuoted && (
            bodyLoading && !email.body_html ? (
              <div className="space-y-2 py-1" aria-label="Loading message body" role="status">
                <div className="h-3 w-11/12 rounded bg-muted animate-pulse" />
                <div className="h-3 w-4/5 rounded bg-muted animate-pulse" />
                <div className="h-3 w-3/5 rounded bg-muted animate-pulse" />
              </div>
            ) : (
              <EmailHtmlBody
                text={email.body_text || email.body_preview}
                attachments={email.attachments}
                zoom={zoom}
                fitToWidth
              />
            )
          )}

          {visible.length > 0 && (
            <div className="mt-3">
              <AttachmentList
                attachments={visible}
                emailId={email.id}
                onPreview={(idx) => onPreview(email.id, idx)}
              />
            </div>
          )}

          {/* Per-message reply row — replies/forwards seed from THIS message,
              so the To/Cc + quoted history are scoped to the message you act
              on (Gmail-style), not always the latest one in the chain. */}
          {onReply && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-2">
              <ThreadReplyButton
                icon={<Reply className="h-3.5 w-3.5" />}
                label="Reply"
                onClick={() => onReply('reply')}
              />
              <ThreadReplyButton
                icon={<ReplyAll className="h-3.5 w-3.5" />}
                label="Reply all"
                onClick={() => onReply('reply_all')}
              />
              <ThreadReplyButton
                icon={<Forward className="h-3.5 w-3.5" />}
                label="Forward"
                onClick={() => onReply('forward')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
